import React from 'react'
import {useParams} from 'react-router-dom'
import {AzPageHeader} from '../../../components/AzPageHeader'
import {isNotEmpty, KTCard, KTCardBody, QUERIES} from '../../../../_dashboard/helpers'
import {KTSVG} from '../../../../_dashboard/helpers/components/KTSVG'
import {useQuery, useQueryClient} from 'react-query'
import {getOneById} from '../../../services/common_requests'
import {useListView} from '../reservation-list/core/ListViewProvider'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import dataAxios from '../../../modules/auth/core/dataAxios'
import {useAuth} from '../../../modules/auth'

export interface DeleteResponse {
  payload?: {
    code?: number
  }
}

const ReservationDetailsPage: React.FC = () => {
  const {id} = useParams<{id: any}>()
  const intl = useIntl()
  const {itemIdForUpdate, setItemIdForUpdate} = useListView()
  const enabledQuery: boolean = isNotEmpty(id)

  const {data: reservation, isLoading} = useQuery(
    `${QUERIES.RESERVATIONS_LIST}-reservation-${id}`,
    () => {
      if (!id) {
        throw new Error('ID is undefined')
      }
      return getOneById(id, '/reservations')
    },
    {
      cacheTime: 0,
      enabled: enabledQuery,
      onError: (err) => {
        setItemIdForUpdate(undefined)
        console.error(err)
      },
    }
  )

  const queryClient = useQueryClient()
  const {auth} = useAuth()
  const currentRole = auth?.role || localStorage.getItem('role') || ''
  const canManage = ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN'].includes(currentRole)

  // Authenticated + smart voucher download (fixes 401 when falling back to guarded /voucher route)
  const handleDownloadVoucher = async () => {
    if (!reservation || !reservation._id) return
    const apiBase = process.env.REACT_APP_API_URL || ''
    const imageBase = process.env.REACT_APP_API_URL_IMAGE || apiBase

    let href: string | null = null
    let isDirectLink = false

    if (reservation.downloadVoucherLink) {
      href = `${imageBase}${reservation.downloadVoucherLink}`
      isDirectLink = true
    } else if (reservation.voucherUrl) {
      href = reservation.voucherUrl
      isDirectLink = true
    } else {
      href = `${apiBase}/reservations/${reservation._id}/voucher`
      isDirectLink = false
    }

    if (!href) return

    if (isDirectLink) {
      window.open(href, '_blank', 'noopener,noreferrer')
      return
    }

    // Guarded route: use authenticated client + blob
    try {
      const resp = await dataAxios.get(href, { responseType: 'blob' })
      const blob = resp.data as Blob
      const objectUrl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objectUrl
      const filename = `voucher-${reservation.orderNumber || reservation._id}.pdf`
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(objectUrl)
    } catch (e: any) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        toast.error(intl.formatMessage({ id: 'ERROR.UNAUTHORIZED', defaultMessage: 'Unauthorized' }))
      } else {
        toast.error(e?.message || intl.formatMessage({ id: 'ERROR.DOWNLOAD_FAILED', defaultMessage: 'Download failed' }))
      }
    }
  }

  // Admin actions for hotel reservations (check availability / confirm / cancel)
  const callAction = async (action: 'check-availability' | 'confirm' | 'cancel') => {
    if (!reservation?._id) return
    const url = `/reservations/${reservation._id}/${action}`
    let body: any = {}
    if (action !== 'confirm') {
      const notes = window.prompt('Notes admin (optionnel) :', '') || ''
      if (notes) body.adminNotes = notes
    }
    try {
      const response = await dataAxios.post(url, body)
      if (action === 'confirm' && response?.data?.data?.confirmationEmailSent === false) {
        toast.warn(
          intl.formatMessage({
            id: 'RESERVATION.CONFIRM_EMAIL_NOT_SENT',
            defaultMessage:
              'Réservation confirmée, mais l’email de confirmation n’a pas pu être envoyé au client.',
          }),
        )
      } else {
        toast.success(
          intl.formatMessage({ id: 'RESERVATION.ACTION_SUCCESS', defaultMessage: 'Action effectuée avec succès' }),
        )
      }
      queryClient.invalidateQueries(`${QUERIES.RESERVATIONS_LIST}-reservation-${id}`)
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Erreur'
      toast.error(msg)
    }
  }

  // Helper functions for status color and icon
  const statusColor = (statusName?: string) => {
    if (!statusName) return 'secondary'

    const status = statusName.toLowerCase()
    if (status.includes('success') || status.includes('confirmed') || status.includes('paid')) {
      return 'success'
    } else if (status.includes('pending') || status.includes('waiting')) {
      return 'warning'
    } else if (
      status.includes('canceled') ||
      status.includes('cancelled') ||
      status.includes('failed')
    ) {
      return 'danger'
    } else if (status.includes('cash')) {
      return 'info'
    }
    return 'secondary'
  }

  const statusIcon = (statusName?: string) => {
    if (!statusName) return '/media/icons/duotune/general/gen005.svg'

    const status = statusName.toLowerCase()
    if (status.includes('success') || status.includes('confirmed') || status.includes('paid')) {
      return '/media/icons/duotune/general/gen048.svg'
    } else if (status.includes('pending') || status.includes('waiting')) {
      return '/media/icons/duotune/general/gen044.svg'
    } else if (
      status.includes('canceled') ||
      status.includes('cancelled') ||
      status.includes('failed')
    ) {
      return '/media/icons/duotune/general/gen040.svg'
    } else if (status.includes('cash')) {
      return '/media/icons/duotune/finance/fin008.svg'
    }
    return '/media/icons/duotune/general/gen005.svg'
  }

  // Styles
  const styles = {
    cardBodyClass: 'py-4 bg-light-gray',
    headerClass: 'bg-light mb-2',
    contentGapClass: 'gap-2',
    headerStyle: {
      borderRadius: '0.7rem',
    },
  }

  if (isLoading) {
    return (
      <div className='d-flex justify-content-center align-items-center min-h-400px'>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    )
  }

  if (!reservation) {
    return (
      <div className='alert alert-danger' role='alert'>
        {intl.formatMessage({id: 'ERROR.RESERVATION_NOT_FOUND'})}
      </div>
    )
  }

  return (
    <>
      <AzPageHeader title={intl.formatMessage({
          id: 'RESERVATION.DETAILS.TITLE',
          defaultMessage: 'Reservation Details',
        })} />

      {/* Header Section */}
      <div
        className='d-flex justify-content-between align-items-center p-4 bg-light mb-2 flex-wrap flex-md-nowrap mx-10'
        style={styles.headerStyle}
      >
        <div className='d-flex align-items-center gap-3 mb-3 mb-md-0'>
          <KTSVG
            path='/media/icons/duotune/general/gen005.svg'
            className='svg-icon-2x text-primary'
          />
          <div>
            <h3 className='mb-1'>Order #{reservation.orderNumber}</h3>
            <div className='text-muted'>{new Date(reservation.createdAt).toLocaleDateString()}</div>
            {/* Quick status badges */}
            <div className='mt-1 d-flex align-items-center gap-2'>
              {reservation.status && (
                <span
                  className="badge fw-bold"
                  style={{ backgroundColor: reservation.status.color || '#6c757d', color: '#fff', fontSize: '0.7rem' }}
                >
                  {reservation.status.name}
                </span>
              )}
              {reservation.externalApiStatus && (
                <span className={`badge ${reservation.externalApiStatus === 'confirmed' ? 'bg-success' : reservation.externalApiStatus === 'unavailable' ? 'bg-danger' : 'bg-info'} text-white`} style={{ fontSize: '0.7rem' }}>
                  External: {reservation.externalApiStatus}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className='d-flex align-items-center gap-3'>
          {/* Download Voucher Button - ONLY when external reservation is validated (confirmed or has bookingId) */}
          {(() => {
            const isExternalValidated = reservation?.externalApiStatus === 'confirmed' || !!reservation?.bookingId;
            const hasVoucherData = !!(reservation?.downloadVoucherLink || reservation?.voucherUrl || reservation?.voucherNum || reservation?.bookingId);
            return (isExternalValidated && hasVoucherData) ? (
              <button
                type='button'
                onClick={handleDownloadVoucher}
                className='btn btn-primary d-flex align-items-center gap-2'
              >
                <KTSVG path='/media/icons/duotune/arrows/arr016.svg' className='svg-icon-1' />
                {intl.formatMessage({
                  id: 'COMMON.DOWNLOAD_VOUCHER',
                  defaultMessage: 'Download Voucher',
                })}
              </button>
            ) : null;
          })()}

          {/* Admin actions for hotel reservations (match prod experience) */}
          {/* Hide all admin action buttons once the external reservation is confirmed or cancelled */}
          {(() => {
            const ext = String(reservation?.externalApiStatus || '').toLowerCase();
            const isTerminal = ext === 'confirmed' || ext === 'cancelled';
            const mainStatusCode = reservation?.status?.code != null ? Number(reservation.status.code) : null;
            // Also consider terminal main statuses (4 = BOOKING_SUCCESS, 8 or 2 = cancelled/failed, etc.)
            const terminalMainStatuses = [2, 4, 8];
            const isTerminalByMainStatus = mainStatusCode != null && terminalMainStatuses.includes(mainStatusCode);
            return canManage && !isTerminal && !isTerminalByMainStatus ? (
              <>
                <button
                  type='button'
                  onClick={() => callAction('check-availability')}
                  className='btn btn-warning d-flex align-items-center gap-2'
                  title='Vérifier la disponibilité via API externe'
                >
                  Vérifier disponibilité
                </button>
                <button
                  type='button'
                  onClick={() => callAction('confirm')}
                  className='btn btn-success d-flex align-items-center gap-2'
                  title='Confirmer / booker via API externe'
                >
                  Confirmer
                </button>
                <button
                  type='button'
                  onClick={() => callAction('cancel')}
                  className='btn btn-danger d-flex align-items-center gap-2'
                  title='Annuler la réservation'
                >
                  Annuler
                </button>
              </>
            ) : null;
          })()}
        </div>
      </div>

      <KTCard>
        <KTCardBody className={styles.cardBodyClass}>
          <div className={`d-flex flex-column ${styles.contentGapClass}`}>
            {/* Hotel Details Section */}
            <div className='m-2 mb-1'>
              <div className='card card-flush py-4'>
                <div className='card-header border-bottom-0 pb-0'>
                  <h3 className='card-title mb-0 d-flex align-items-center'>
                    <KTSVG
                      path='/media/icons/duotune/general/gen022.svg'
                      className='svg-icon-2x text-primary me-3'
                    />
                    {intl.formatMessage({id: 'HOTEL.DETAILS', defaultMessage: 'Hotel Details'})}
                  </h3>
                </div>
                <div className='card-body pt-4'>
                  <div className='row g-4 align-items-start'>
                    {/* Hotel Image */}
                    <div className='col-lg-4 col-md-5'>
                      {reservation.hotel?.image?.url && (
                        <div className='position-relative'>
                          <img
                            src={
                              (process.env.REACT_APP_API_URL_IMAGE || '') + reservation.hotel.image.url
                            }
                            alt={reservation.hotel.name}
                            style={{
                              width: '100%',
                              height: 200,
                              objectFit: 'cover',
                              borderRadius: 12,
                              boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                            }}
                            className='mb-3'
                          />
                        </div>
                      )}
                    </div>
                    
                    {/* Hotel Information */}
                    <div className='col-lg-8 col-md-7'>
                      <div className='d-flex flex-column gap-4'>
                        {/* Hotel Name - Prominent Display */}
                        {reservation.hotel?.name && (
                          <div className='border-bottom pb-3'>
                            <h4 className='text-gray-900 fw-bold mb-2'>
                              {reservation.hotel.name}
                            </h4>
                            <div className='d-flex align-items-center text-muted'>
                              <KTSVG
                                path='/media/icons/duotune/general/gen022.svg'
                                className='svg-icon-1 text-info me-2'
                              />
                              <span className='fs-7'>Hotel Information</span>
                            </div>
                          </div>
                        )}
                        
                        {/* Hotel Details Grid */}
                        <div className='row g-3'>
                          {/* Hotel ID */}
                          {reservation.hotel?.hotel_id && (
                            <div className='col-sm-6'>
                              <div className='d-flex align-items-center p-3 bg-light-primary rounded-3'>
                                <div className='symbol symbol-40px me-3'>
                                  <div className='symbol-label bg-primary'>
                                    <KTSVG
                                      path='/media/icons/duotune/general/gen040.svg'
                                      className='svg-icon-1 text-white'
                                    />
                                  </div>
                                </div>
                                <div>
                                  <div className='text-gray-600 fs-7 fw-semibold'>Hotel ID</div>
                                  <div className='text-gray-900 fw-bold'>{reservation.hotel.hotel_id}</div>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* Booking ID */}
                          {reservation.bookingId && (
                            <div className='col-sm-6'>
                              <div className='d-flex align-items-center p-3 bg-light-success rounded-3'>
                                <div className='symbol symbol-40px me-3'>
                                  <div className='symbol-label bg-success'>
                                    <KTSVG
                                      path='/media/icons/duotune/general/gen014.svg'
                                      className='svg-icon-1 text-white'
                                    />
                                  </div>
                                </div>
                                <div>
                                  <div className='text-gray-600 fs-7 fw-semibold'>Booking ID</div>
                                  <div className='text-gray-900 fw-bold'>#{reservation.bookingId}</div>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* Voucher Number */}
                          {reservation.voucherNum && (
                            <div className='col-sm-6'>
                              <div className='d-flex align-items-center p-3 bg-light-warning rounded-3'>
                                <div className='symbol symbol-40px me-3'>
                                  <div className='symbol-label bg-warning'>
                                    <KTSVG
                                      path='/media/icons/duotune/files/fil024.svg'
                                      className='svg-icon-1 text-white'
                                    />
                                  </div>
                                </div>
                                <div>
                                  <div className='text-gray-600 fs-7 fw-semibold'>Voucher</div>
                                  <div className='text-gray-900 fw-bold'>{reservation.voucherNum}</div>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* Order Number */}
                          {reservation.orderNumber && (
                            <div className='col-sm-6'>
                              <div className='d-flex align-items-center p-3 bg-light-info rounded-3'>
                                <div className='symbol symbol-40px me-3'>
                                  <div className='symbol-label bg-info'>
                                    <KTSVG
                                      path='/media/icons/duotune/general/gen005.svg'
                                      className='svg-icon-1 text-white'
                                    />
                                  </div>
                                </div>
                                <div>
                                  <div className='text-gray-600 fs-7 fw-semibold'>Order Number</div>
                                  <div className='text-gray-900 fw-bold'>#{reservation.orderNumber}</div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className='row g-2 m-2'>
              {/* Reservation Details and Price Details combined */}
              <div className='col-12 mb-2'>
                <div className='card card-flush py-4'>
                  <div className='card-body pt-0'>
                    <div className='row g-2'>
                      {/* Left Column - Reservation Details */}
                      <div className='col-lg-6 custom-border-lg-end pe-3'>
                        <h3 className='card-title mb-3'>
                          {intl.formatMessage({
                            id: 'RESERVATION.DETAILS.TITLE',
                            defaultMessage: 'Reservation Details',
                          })}
                        </h3>
                        <div className='table-responsive'>
                          <table className='table align-middle fs-6 gy-5'>
                            <tbody>
                              <tr>
                                <td className='text-gray-800'>
                                  <KTSVG
                                    path='/media/icons/duotune/general/gen018.svg'
                                    className='svg-icon-1 me-2 text-success'
                                  />
                                  Check-in Date
                                </td>
                                <td>{new Date(reservation.checkInDate).toLocaleDateString()}</td>
                              </tr>
                              <tr>
                                <td className='text-gray-800'>
                                  <KTSVG
                                    path='/media/icons/duotune/general/gen018.svg'
                                    className='svg-icon-1 me-2 text-danger'
                                  />
                                  Check-out Date
                                </td>
                                <td>{new Date(reservation.checkOutDate).toLocaleDateString()}</td>
                              </tr>
                              <tr>
                                <td className='text-gray-800'>
                                  <KTSVG
                                    path='/media/icons/duotune/finance/fin008.svg'
                                    className='svg-icon-1 me-2 text-info'
                                  />
                                  Payment Method
                                </td>
                                <td>
                                  {reservation.paymentMethod?.name ||
                                    reservation.paymentMethod?.description ||
                                    reservation.paymentMethod?.code ||
                                    (reservation.paymentMethod?.translations && reservation.paymentMethod.translations[0]?.name) ||
                                    '-'}
                                </td>
                              </tr>
                              {/* Reservation status (main + external API) */}
                              <tr>
                                <td className='text-gray-800'>
                                  <KTSVG
                                    path='/media/icons/duotune/general/gen044.svg'
                                    className='svg-icon-1 me-2 text-warning'
                                  />
                                  Status
                                </td>
                                <td>
                                  {reservation.status ? (
                                    <span
                                      className="badge fw-bold"
                                      style={{
                                        backgroundColor: reservation.status.color || '#6c757d',
                                        color: '#fff',
                                        padding: '0.25rem 0.5rem',
                                        borderRadius: '4px',
                                      }}
                                    >
                                      {reservation.status.name}
                                    </span>
                                  ) : (
                                    '-'
                                  )}
                                </td>
                              </tr>
                              {reservation.externalApiStatus && (
                                <tr>
                                  <td className='text-gray-800'>
                                    <KTSVG
                                      path='/media/icons/duotune/general/gen005.svg'
                                      className='svg-icon-1 me-2 text-muted'
                                    />
                                    External API Status
                                  </td>
                                  <td>
                                    <span className={`badge ${reservation.externalApiStatus === 'confirmed' ? 'bg-success' : reservation.externalApiStatus === 'unavailable' ? 'bg-danger' : 'bg-warning'} text-white`}>
                                      {reservation.externalApiStatus}
                                    </span>
                                  </td>
                                </tr>
                              )}
                              {reservation.confirmationEmailSentAt && (
                                <tr>
                                  <td className='text-gray-800'>
                                    <KTSVG
                                      path='/media/icons/duotune/communication/com010.svg'
                                      className='svg-icon-1 me-2 text-muted'
                                    />
                                    {intl.formatMessage({
                                      id: 'RESERVATION.CONFIRMATION_EMAIL_SENT',
                                      defaultMessage: 'Email de confirmation client',
                                    })}
                                  </td>
                                  <td>
                                    {new Date(reservation.confirmationEmailSentAt).toLocaleString()}
                                  </td>
                                </tr>
                              )}
                              <tr>
                                <td className='text-gray-800'>
                                  <KTSVG
                                    path='/media/icons/duotune/general/gen014.svg'
                                    className='svg-icon-1 me-2 text-muted'
                                  />
                                  Created Date
                                </td>
                                <td>{new Date(reservation.createdAt).toLocaleString()}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Right Column - Price Details */}
                      <div className='col-lg-6 ps-3'>
                        <h3 className='card-title mb-3'>
                          {intl.formatMessage({
                            id: 'PRICE.DETAILS',
                            defaultMessage: 'Price Details',
                          })}
                        </h3>
                        <div className='d-flex flex-column gap-6 mt-8'>
                          <div className='d-flex justify-content-between'>
                            <span className='text-gray-600'>
                              <KTSVG
                                path='/media/icons/duotune/finance/fin006.svg'
                                className='svg-icon-1 me-2 text-warning'
                              />
                              Total Price
                            </span>
                            <span className='text-gray-800 fw-bold text-warning'>
                              {reservation.totalPrice} TND
                            </span>
                          </div>
                          <div className='d-flex justify-content-between'>
                            <span className='text-gray-600'>
                              <KTSVG
                                path='/media/icons/duotune/finance/fin007.svg'
                                className='svg-icon-1 me-2 text-primary'
                              />
                              Total to Pay
                            </span>
                            <span className='text-gray-800 fw-bold text-primary'>
                              {reservation.totalToPay} TND
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Guest Information */}
              <div className='col-12'>
                <div className='card card-flush py-4'>
                  <div className='card-header'>
                    <h3 className='card-title'>
                      {intl.formatMessage({
                        id: 'GUEST.INFORMATION',
                        defaultMessage: 'Guest Information',
                      })}
                    </h3>
                  </div>
                  <div className='card-body pt-0'>
                    <div className='table-responsive'>
                      <table className='table align-middle table-row-dashed fs-6 gy-5'>
                        <thead>
                          <tr className='text-start text-muted fw-bold fs-7 text-uppercase gs-0'>
                            <th>
                              {intl.formatMessage({id: 'GUEST.NAME', defaultMessage: 'Guest Name'})}
                            </th>
                            <th>
                              {intl.formatMessage({id: 'GUEST.EMAIL', defaultMessage: 'Email'})}
                            </th>
                            <th>
                              {intl.formatMessage({id: 'GUEST.PHONE', defaultMessage: 'Phone'})}
                            </th>
                            <th>
                              {intl.formatMessage({id: 'GUEST.ID_LOG', defaultMessage: 'ID Log'})}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>
                              <KTSVG
                                path='/media/icons/duotune/general/gen021.svg'
                                className='svg-icon-1 me-2 text-info'
                              />
                              {reservation.civility} {reservation.name} {reservation.surname}
                            </td>
                            <td>
                              <KTSVG
                                path='/media/icons/duotune/communication/com011.svg'
                                className='svg-icon-1 me-2 text-success'
                              />
                              {reservation.email}
                            </td>
                            <td>
                              <KTSVG
                                path='/media/icons/duotune/communication/com012.svg'
                                className='svg-icon-1 me-2 text-primary'
                              />
                              {reservation.phone}
                            </td>
                            <td>
                              <KTSVG
                                path='/media/icons/duotune/general/gen014.svg'
                                className='svg-icon-1 me-2 text-muted'
                              />
                              {reservation.idLog}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </KTCardBody>
      </KTCard>
    </>
  )
}

export {ReservationDetailsPage}
